import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { t } from "../lib/i18n";

export const fmtEur = (v) => {
  if (v === null || v === undefined || v === "" || isNaN(Number(v))) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1).replace(".", ",") + " M€";
  if (Math.abs(n) >= 1e3) return Math.round(n / 1e3) + " k€";
  return n.toLocaleString("es-ES") + " €";
};

const fmtPct = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v).toLocaleString("es-ES")}%`);

const NAVY = "#1E3A5F";
const NAVY_PROJ = "#E0712C";
const GOLD = "#E4B54B";
const GOLD_PROJ = "#F1D48F";
const FOREST = "#2E6F80";
const FOREST_PROJ = "#8FBFCB";

export const ImFinancialViz = ({ kpis, projection }) => {
  const chartData = (projection?.years || []).map((y) => ({
    label: `${y.year}${y.projected ? " (P)" : ""}`,
    Ingresos: y.revenue,
    "Margen bruto": y.gross ?? null,
    EBITDA: y.ebitda,
    projected: y.projected,
  }));
  const hasGross = chartData.some((d) => d["Margen bruto"] != null);

  return (
    <div data-testid="im-financial-viz">
      <div className="grid grid-cols-2 md:grid-cols-4 hairline bg-card">
        <Kpi label={t("Ingresos (último ej.)")} value={fmtEur(kpis?.revenue)} testid="kpi-revenue" />
        <Kpi label={t("EBITDA")} value={fmtEur(kpis?.ebitda)} border testid="kpi-ebitda" />
        <Kpi label={t("Margen EBITDA")} value={fmtPct(kpis?.ebitda_margin_pct)} border testid="kpi-margin" />
        <Kpi label={t("Empleados")} value={kpis?.employees ?? "—"} border testid="kpi-employees" />
      </div>

      {chartData.length > 0 && (
        <div className="hairline hairline-t-0 bg-card p-6" data-testid="im-projection-chart">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-serif-display text-2xl">{t("Evolución y proyección financiera")}</h3>
            <span className="font-mono-label text-muted-foreground">{t("Valores en € · (P) = Proyección · crecimiento")}{" "}{fmtPct(projection?.growth_pct)}{" "}{t("anual")}</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d7e0ea" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtEur(v)} tick={{ fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} width={70} />
              <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ borderRadius: 0, border: "1px solid #d7e0ea", fontSize: 12 }} />
              <Bar dataKey={t("Ingresos")} radius={[0, 0, 0, 0]}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.projected ? NAVY_PROJ : NAVY} />
                ))}
              </Bar>
              {hasGross && (
                <Bar dataKey={t("Margen bruto")} radius={[0, 0, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.projected ? GOLD_PROJ : GOLD} />
                  ))}
                </Bar>
              )}
              <Bar dataKey={t("EBITDA")} radius={[0, 0, 0, 0]}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.projected ? FOREST_PROJ : FOREST} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center justify-center gap-5 mt-3 text-xs" data-testid="im-chart-legend">
            <LegendItem color={NAVY} label={t("Ingresos")} />
            {hasGross && <LegendItem color={GOLD} label={t("Margen bruto")} />}
            <LegendItem color={FOREST} label={t("EBITDA")} />
            <LegendItem color={NAVY_PROJ} label={t("Ingresos (proyección)")} />
            {hasGross && <LegendItem color={GOLD_PROJ} label={t("Margen bruto (proyección)")} />}
            <LegendItem color={FOREST_PROJ} label={t("EBITDA (proyección)")} />
          </div>
        </div>
      )}

      <div className="hairline hairline-t-0 bg-card grid grid-cols-2 md:grid-cols-5 text-sm" data-testid="im-financial-table">
        <MiniFact label={t("Deuda financiera neta")} value={fmtEur(kpis?.net_debt)} />
        <MiniFact label={t("Ingresos recurrentes")} value={fmtPct(kpis?.recurring_revenue_pct)} border />
        <MiniFact label={t("Top-3 clientes")} value={fmtPct(kpis?.top3_client_pct)} border />
        <MiniFact label={t("CAPEX anual medio")} value={fmtEur(kpis?.capex_avg)} border />
        <MiniFact label={t("Crecimiento proyectado")} value={fmtPct(kpis?.growth_pct)} border />
      </div>
    </div>
  );
};

const LegendItem = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-3 h-3 inline-block" style={{ backgroundColor: color }} />
    <span className="text-muted-foreground">{label}</span>
  </span>
);

const Kpi = ({ label, value, border, testid }) => (
  <div className={`p-5 ${border ? "hairline border-r-0 border-t-0 border-b-0" : ""}`} data-testid={testid}>
    <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{label}</div>
    <div className="font-serif-display text-2xl md:text-3xl mt-1">{value}</div>
  </div>
);

const MiniFact = ({ label, value, border }) => (
  <div className={`px-5 py-4 ${border ? "hairline border-r-0 border-t-0 border-b-0" : ""}`}>
    <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{label}</div>
    <div className="font-serif-display text-lg mt-0.5">{value}</div>
  </div>
);
